import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import useVendorAccessGate from "../../../hooks/useVendorAccessGate";
import { useAuth } from "../../../services/providers/authProvider";
import { useTicketUnread } from "../../../context/ticketUnreadContext";
import ticketService from "../../../services/ticketService";
import { cacheTicketDescription, hasOpenTickets, mergeTicketSnapshots } from "../../../services/ticketUtils";
import { dispatchAppError } from "../../../services/errors/errorDispatcher";
import { ValidationError } from "../../../services/errors/ValidationError";
import SuccessFeedbackDialog from "../../Shared/successFeedbackDialog";
import ConfirmationModal from "../../Shared/confirmationModal";
import { NewTicketDialog, NewTicketPanel } from "./newTicket";
import TicketCard from "./ticketCard";

const Ticket = () => {
  useVendorAccessGate();
  const { isImpersonated } = useAuth() || {};
  const { ticketSnapshots, refreshSummary } = useTicketUnread();
  const writeDisabled = Boolean(isImpersonated);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTicketDialogOpen, setNewTicketDialogOpen] = useState(false);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogText, setErrorDialogText] = useState("");

  const showInlineNewTicket = useMemo(() => !loading && !hasOpenTickets(tickets), [loading, tickets]);
  const showNewTicketButton = useMemo(() => !loading && hasOpenTickets(tickets) && !writeDisabled, [loading, tickets, writeDisabled]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketService.listTickets({ sortBy: "createdAt", sortDirection: "desc" });
      setTickets(res.items);
    } catch (e) {
      if (!(e instanceof ValidationError)) {
        dispatchAppError(e?.response?.data?.message ?? e?.message ?? "Errore caricamento ticket");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (!ticketSnapshots.length) return;
    setTickets((prev) => mergeTicketSnapshots(prev, ticketSnapshots));
  }, [ticketSnapshots]);

  const handleCreateTicket = async (payload) => {
    if (writeDisabled) return;
    setIsSubmitting(true);
    try {
      const res = await ticketService.createTicket(payload);
      cacheTicketDescription(res.ticketId, payload.description.trim());
      setNewTicketDialogOpen(false);
      setExpandedTicketId(res.ticketId);
      await loadTickets();
      setIsSuccessDialogOpen(true);
    } catch (e) {
      const msg = e?.response?.data?.message ?? e?.message ?? "Si è verificato un errore nell'invio del ticket.";
      setErrorDialogText(msg);
      setErrorDialogOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleTicket = (ticketId) => {
    setExpandedTicketId((prev) => (prev === ticketId ? null : ticketId));
  };

  const handleMessagesRead = useCallback(() => {
    setTickets((prev) =>
      prev.map((t) =>
        t.id === expandedTicketId && t.unreadCount > 0 ? { ...t, unreadCount: 0 } : t
      )
    );
    refreshSummary();
  }, [expandedTicketId, refreshSummary]);

  return (
    <Box component="main" display="flex" flexDirection="column" sx={{ width: "100%" }}>
        <Typography variant="h4" sx={{ fontFamily: "Rubik", fontWeight: "bold", color: "#0b3d87", mb: 1 }}>
          Ticket assistenza
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Typography sx={{ fontFamily: "Rubik", color: "#808080", flex: 1, minWidth: 0 }}>
            Crea un nuovo ticket o consulta lo storico delle segnalazioni aperte con il team di supporto.
          </Typography>
          {showNewTicketButton ? (
            <Button
              variant="contained"
              onClick={() => setNewTicketDialogOpen(true)}
              sx={{
                fontFamily: "Rubik",
                fontWeight: "bold",
                textTransform: "none",
                borderRadius: "999px",
                backgroundColor: "#0b3d87",
                flexShrink: 0,
                whiteSpace: "nowrap",
                px: 2.5,
                "&:hover": { backgroundColor: "#092b6a" },
              }}
            >
              + Apri un nuovo ticket
            </Button>
          ) : null}
        </Box>

        {showInlineNewTicket ? (
          <NewTicketPanel onSubmit={handleCreateTicket} disabled={writeDisabled} isSubmitting={isSubmitting} />
        ) : null}

        <NewTicketDialog
          open={newTicketDialogOpen}
          onClose={() => setNewTicketDialogOpen(false)}
          onSubmit={handleCreateTicket}
          disabled={writeDisabled}
          isSubmitting={isSubmitting}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              expanded={expandedTicketId === ticket.id}
              onToggle={() => handleToggleTicket(ticket.id)}
              disabled={writeDisabled}
              onMessagesRead={expandedTicketId === ticket.id ? handleMessagesRead : undefined}
            />
          ))
        )}

        <SuccessFeedbackDialog
          open={isSuccessDialogOpen}
          onOk={() => setIsSuccessDialogOpen(false)}
          title="Ticket inviato"
          message="La tua richiesta è stata registrata. Riceverai una risposta appena il ticket passa in lavorazione."
          confirmText="Ok"
          icon={<CheckCircleOutline sx={{ color: "white", fontSize: 44 }} />}
        />
        <ConfirmationModal
          open={errorDialogOpen}
          onClose={() => setErrorDialogOpen(false)}
          title="Errore!"
          subtitle=""
          message={errorDialogText}
          confirmColor="#e00036"
          confirmText="OK"
          hideCancelButton
          onConfirm={() => setErrorDialogOpen(false)}
        />
    </Box>
  );
};

export default Ticket;
